// src/lib/planTasks.ts

export type Category = "Personal" | "Legal" | "Financial";
export type TaskStatus = "completed" | "next" | "incomplete";

export type PlanTask = {
  id: string;
  title: string;
  description: string;
  category: Category;
  icon: string;
  status: TaskStatus;
  route: string;
  summaryRoute: string;
};

export type BuildPlanTasksDeps = {
  isTaskComplete: (id: string) => boolean;

  // booleans your Frame140 already has
  memoryCollectionsComplete: boolean;
  inventoryCompleted: boolean;
  propertiesTaskComplete: boolean;
  funeralCompleted: boolean;
  legalEssentialsCompleted: boolean;
};

export function buildPlanTasks(deps: BuildPlanTasksDeps): {
  mainTasks: PlanTask[];
  additionalTasks: PlanTask[];
} {
  const {
    isTaskComplete,
    memoryCollectionsComplete,
    inventoryCompleted,
    propertiesTaskComplete,
    funeralCompleted,
    legalEssentialsCompleted,
  } = deps;

  const mainTasks: PlanTask[] = [
    {
      id: "about",
      title: "Setup Your Plan",
      description:"Add your personal details, trusted loved ones, and important passwords.",
      status: isTaskComplete("about") ? "completed" : "next",
      category: "Personal",
      icon: "heart",
      route: "/frame-132",
      summaryRoute: "/frame-135",
    },
    {
      id: "legacy",
      title: "Leave Behind Memories and Messages",
      description: "Assemble a digital keepsake for your family and loved ones.",
      status:
        isTaskComplete("legacy") || memoryCollectionsComplete
          ? "completed"
          : "next",
      category: "Personal",
      icon: "heart",
      route: "/frame-157",
      summaryRoute: "/frame-173",
    },
    {
      id: "inventory",
      title: "Inventory of Assets",
      description: "Create a clear picture of your financial accounts and assets.",
      status:
        inventoryCompleted ||
        propertiesTaskComplete ||
        isTaskComplete("inventory")
          ? "completed"
          : "next",
      category: "Financial",
      icon: "bank",
      route: "/frame-174",
      summaryRoute: "/frame-213",
    },
    {
      id: "funeral",
      title: "Plan for Your Funeral and Memorial",
      description:
        "Share your preferences for your funeral, memorial, or celebration of life.",
      status: funeralCompleted ? "completed" : "next",
      category: "Personal",
      icon: "heart",
      route: "/frame-219",
      summaryRoute: "/frame-223",
    },
    {
      id: "personal",
      title: "Organize Personal Accounts and Belongings",
      description:"Document personal accounts, belongings, and instructions for loved ones.",
      status: isTaskComplete("personal") ? "completed" : "next",
      category: "Personal",
      icon: "bank",
      route: "/frame-800",
      summaryRoute: "/frame-802",
    },
    {
      id: "legal_essentials",
      title: "Complete Your Legal Essentials",
      description: "Upload important legal documents and choose your executor.",
      status: legalEssentialsCompleted ? "completed" : "next",
      category: "Legal",
      icon: "bank",
      route: "/frame-260",
      summaryRoute: "/frame-215",
    },
    // Professional Support hidden until implemented
    // {
    //   id: 'professional',
    //   title: 'Get Professional Support',
    //   subtitle: '3 tasks, 1 complete',
    //   status: isTaskComplete('professional') ? 'completed' : 'next',
    //   category: 'Legal',
    //   icon: 'heart',
    //   route: '/frame-182'
    // }
  ];

  const additionalTasks: PlanTask[] = [
    {
      id: "debts",
      title: "Track Debts and Final Expenses",
      description: "Keep track of outstanding debts and expected final expenses.",
      category: "Financial",
      status: isTaskComplete("debts") ? "completed" : "incomplete",
      icon: "bank",
      route: "/frame-234",
      summaryRoute: "/frame-238",
    },
    {
      id: "distribution",
      title: "Assign and Approve Asset Distribution",
      description: "Outline how assets should be distributed and approved.",
      category: "Financial",
      status: isTaskComplete("distribution") ? "completed" : "incomplete",
      icon: "bank",
      route: "/frame-245",
      summaryRoute: "/frame-247",
    },
    {
      id: "pets",
      title: "Plan for Your Pets and Dependents",
      description: "Document care plans for those who depend on you.",
      category: "Personal",
      status: isTaskComplete("pets") ? "completed" : "incomplete",
      icon: "heart",
      route: "/frame-241",
      summaryRoute: "/frame-248",
    },
  ];

  return { mainTasks, additionalTasks };
}